const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mPhoneNumberField.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import _ from 'lodash';
import { useCIS4mData, useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { ValidationFunction } from '../../../components/Forms/utils/validation';
import { MuiTelInput, MuiTelInputCountry } from 'mui-tel-input';
import { TextInput, useText } from '../../../utils/intlUtils';
import { useCIS4mContext } from '../context';
import { CountryCode, getCountryCallingCode, validatePhoneNumberLength } from 'libphonenumber-js';

interface Props extends CIS4mValueComponentProps {
    label: TextInput;
    required?: boolean;
    className?: string;
    countryCode?: string;
    countryCodePath?: string; // Path to field that the country code is taken from (if there is no value specified)
    helperText?: string;
    checkValidity?: ValidationFunction;
}

export const CIS4mPhoneNumberField = forwardRef((props: Props, ref: any) => {
    const { path, initialValue, helperText, className, checkValidity } = props;

    const { variant } = useCIS4mContext().style;

    const disabled = props.disabled ?? false;
    const hidden = props.hidden ?? false;
    const required = props.required ?? false;
    const readonly = props.readonly ?? false;

    const label = useText(props.label);

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        checkValidity,
        disabled,
        hidden,
        initialValue,
        path,
        required,
    });

    const formData = useCIS4mData();
    const defaultCountryCode = props.countryCode || 'CH';
    const selectedCountryCode = (
        props.countryCodePath ? _.get(formData, props.countryCodePath) || defaultCountryCode : defaultCountryCode
    ) as CountryCode;

    // Prefill the field with the country's calling code (e.g. '+41') when there is no value yet.
    const callingCode = getCountryCallingCode(selectedCountryCode);

    const handleChange = (newValue: string) => {
        // mui-tel-input does not limit the length itself, so reject any input that exceeds
        // the valid length for the entered country (keeps the controlled value unchanged).
        if (validatePhoneNumberLength(newValue, selectedCountryCode) === 'TOO_LONG') {
            return;
        }

        setValue(newValue || undefined);
    };

    return hidden ? null : (
        <MuiTelInput
            defaultCountry={selectedCountryCode as MuiTelInputCountry}
            disableDropdown
            onChange={handleChange}
            value={value ?? '+' + callingCode}
            fullWidth
            variant={variant}
            className={className}
            color={!!errorMessage ? 'error' : 'primary'}
            error={!!errorMessage}
            required={required}
            label={label}
            disabled={disabled}
            helperText={errorMessage || helperText}
            slotProps={{
                // Remove mui-tel-input's flag adornment entirely so it leaves no extra space.
                input: {
                    startAdornment: null,
                },
                htmlInput: {
                    readOnly: readonly,
                },
            }}
        />
    );
});

CIS4mPhoneNumberField.displayName = 'CIS4mPhoneNumberField';
